import { useEffect, useState } from "react";
import { useRole } from "../MangeContextProvide";
import { fetchOrganisations, fetchCommunicationErrors } from "./communicationError.service";
import type { CommunicationError, Organisation, Pagination } from "./types";

export function useCommunicationErrors() {
  const { organizationId, role } = useRole() as any;
  const isAdmin = role === "admin";

  const [errors, setErrors] = useState<CommunicationError[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [orgLoading, setOrgLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const activeOrgId = isAdmin ? selectedOrgId : organizationId;

  // org list (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setOrgLoading(true);
      try {
        const list = await fetchOrganisations();
        setOrganisations(list);
        setSelectedOrgId("");
      } catch {
        /* silent */
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [isAdmin]);

  const load = async (p = page, l = limit, search = debouncedSearch) => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetchCommunicationErrors(activeOrgId, p, l, search);
      setErrors(res.data);
      setPagination(res.pagination);
    } catch {
      setFetchError("Unable to reach the server. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  // debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(h);
  }, [searchQuery]);

  // refetch on search / org (reset to page 1)
  useEffect(() => {
    setPage(1);
    load(1, limit, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeOrgId]);

  // refetch on page change
  useEffect(() => {
    load(page, limit, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return {
    isAdmin,
    errors, pagination, loading, fetchError,
    page, setPage, limit,
    organisations, selectedOrgId, setSelectedOrgId, orgLoading,
    searchQuery, setSearchQuery,
  };
}